"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { useTheme } from "next-themes";
import { flushSync } from "react-dom";
import { getTheme, huellenVars, type Theme } from "../lib/themes";
import { VOICE_ORB_DEFAULT, leseVoiceOrb, type VoiceOrbConfig } from "../lib/voice-orb";
import {
  SYMBOL_STIL_VORGABE,
  istSymbolStil,
  type SymbolStil,
} from "../components/ui/file-icons/stile";
import {
  verbraucheThemeVomWirt,
  farbthemaAusUrl,
  isEmbedFarbthemaMessage,
  istFarbthemaId,
} from "../lib/embed-theme";
import { EMBED_PARENT_ORIGIN_SUFFIXES } from "../lib/embed-refresh";
import { EMBED_FLAECHEN_VARIABLEN, flaecheIstUebernommen } from "../lib/embed-flaeche";
import { isAllowedEmbedOrigin } from "../hooks/useEmbeddedThemeBridge";
import {
  THEME_COOKIE,
  COLOR_THEME_COOKIE,
  LOGO_HOVER_COOKIE,
  SYMBOL_STIL_COOKIE,
  VOICE_ORB_COOKIE,
  THEME_TRANSITION_COOKIE,
  THEME_OVERRIDES_COOKIE,
  THEME_OVERRIDES_COOKIE_MAX,
  readThemeCookie,
  writeThemeCookie,
  deleteThemeCookie,
  setzeKeksAbschottung,
  darfKeksSchreiben,
} from "../lib/theme-cookie";
import {
  istHuellenSchluessel,
  liesHuelle,
  schreibHuelle,
  type HuellenEinstellung,
} from "../lib/huellen-einstellung";
import { pickCookieThemeUpdate, shouldAdoptRemoteDarkMode } from "../lib/theme-sync";
import type { ThemeMode } from "../lib/theme-ssr";
import {
  DEFAULT_THEME_TRANSITION,
  readThemeTransition,
  isThemeTransition,
  isThemeTransitionBusy,
  runThemeTransition,
  trackPointerOrigin,
  writeThemeTransition,
  THEME_TRANSITION_LS_KEY,
  type ThemeTransition,
} from "../lib/theme-transition";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ThemeOverrides {
  light: Partial<Record<string, string>>;
  dark: Partial<Record<string, string>>;
}

interface ColorThemeContextValue {
  /** Current color theme ID. */
  colorThemeId: string;
  /** Switch to a different color theme. */
  setColorTheme: (id: string, originEl?: Element | null) => void;
  /** Per-variable overrides (user customizations). */
  customOverrides: ThemeOverrides;
  /** Set a single CSS variable override. */
  setOverride: (mode: "light" | "dark", key: string, value: string) => void;
  /**
   * Die Huelle, wie der NUTZER sie gewaehlt hat — `null` heisst „die App
   * entscheidet" und ist kein Fehlerfall (`lib/huellen-einstellung.ts`).
   */
  huelle: HuellenEinstellung | null;
  /**
   * Huelle waehlen. Schreibt alle fuenf Schalter in EINEM Zug und fuer BEIDE
   * Modi — „mein Theme hat eine Klammer" ist keine Aussage ueber die
   * Tageszeit. Ein Aufruf je Schalter waere zehn Speichervorgaenge und ein
   * Zwischenzustand, in dem die Schale schon wechselte und die Zier noch
   * nicht.
   */
  setHuelle: (wahl: HuellenEinstellung) => void;
  /** Reset all custom overrides. */
  resetOverrides: () => void;
  /** Whether the logo fade-on-hover animation is enabled (appearance setting). */
  logoHoverAnim: boolean;
  /** Symbol-Stil des Dateimanagers — app-uebergreifend wie das Farb-Theme. */
  symbolStil: SymbolStil;
  setSymbolStil: (stil: SymbolStil) => void;
  /** Sprachvisualisierung — app-übergreifend, wie Farb-Theme und Logo-Animation. */
  voiceOrb: VoiceOrbConfig;
  setVoiceOrb: (cfg: VoiceOrbConfig) => void;
  /** Toggle the logo fade-on-hover animation (persisted + cross-app synced). */
  setLogoHoverAnim: (on: boolean) => void;
  /** Effekt beim Theme-/Modus-Wechsel (geräte-lokal). */
  themeTransition: ThemeTransition;
  setThemeTransition: (v: ThemeTransition) => void;
  /**
   * Hell/Dunkel umschalten MIT Übergangseffekt. Umschalter sollen das statt
   * `setTheme` aus next-themes benutzen — sonst bleibt der Wechsel ein harter
   * Schnitt. Programmatische Wechsel (iframe-Bridge, Profil-Sync) rufen weiter
   * direkt `setTheme` auf: ohne Zeiger-Ursprung wäre eine Welle sinnlos.
   */
  setMode: (mode: ThemeMode, originEl?: Element | null) => void;
  /**
   * Der Modus, auf den gerade umgeschaltet WIRD — sofort ab dem Klick, nicht
   * erst nach der Animation. Oberflaechen, die den Modus anzeigen (Umschalter-
   * Icon, aktive Kachel im Design-Tab), muessen den lesen: `resolvedTheme`
   * folgt erst, wenn `setTheme()` am Ende der Welle laeuft, und das Icon kippte
   * dadurch eine halbe Sekunde zu spaet. Schlimmer noch: ein zweiter schneller
   * Klick berechnete aus dem veralteten Wert DASSELBE Ziel wie der erste.
   */
  pendingMode: ThemeMode | null;
  /** Modus, wie er im `apply()` der Animation gesetzt wurde — s. State-Kommentar. */
  appliedDark: boolean | null;
  /**
   * Ist das Design-Schnellpanel offen? Der Zustand liegt hier, weil ihn zwei
   * unabhaengige Ausloeser setzen (Rechtsklick auf den Schalter, Eintrag im
   * Benutzermenue) und ihn genau EIN Renderer liest (TopNav bzw. HubLauncher).
   * Bewusst nur ein Boolean: haette der Provider das Panel selbst gerendert,
   * waere es ein Zirkel-Import — das Panel ruft `useColorTheme()` aus dieser Datei.
   */
  designPanelOpen: boolean;
  setDesignPanelOpen: (v: boolean) => void;
  /**
   * Registriert einen Renderer des Design-Schnellpanels (TopNav, HubLauncher)
   * und liefert die Abmeldefunktion (fuer den Effekt-Cleanup). `ThemeToggleButton`
   * sitzt auch auf Seiten OHNE Panel-Renderer (StatusDashboard, ApiExplorer,
   * MobileNavAccountFooter) — dort wuerde ein Rechtsklick das native Kontextmenue
   * unterdruecken, ohne dass je etwas erscheint. Der Zaehler statt Boolean, weil
   * TopNav und HubLauncher potenziell gleichzeitig gemountet sein koennen
   * (eingebettete Ansichten) und ein einfaches Boolean beim Unmount des einen
   * faelschlich auch den Renderer des anderen abmelden wuerde.
   */
  registerDesignPanelHost: () => () => void;
  /** Existiert irgendwo ein gemounteter Renderer des Design-Panels? */
  designPanelAvailable: boolean;
}

const ColorThemeContext = createContext<ColorThemeContextValue | null>(null);

// ---------------------------------------------------------------------------
// Storage keys
// ---------------------------------------------------------------------------

const LS_THEME_KEY = "janeway-color-theme";
const LS_OVERRIDES_KEY = "janeway-theme-overrides";

const LS_LOGO_HOVER_KEY = "janeway-logo-hover";
const LS_VOICE_ORB_KEY = "janeway-voice-orb";

/**
 * Overrides ablegen — localStorage UND Cookie, immer zusammen.
 *
 * Der Cookie ist die einzige der drei Ablagen, die der Server beim Rendern
 * sieht; ohne ihn zeigt der erste Paint das unveraenderte Farbschema und kippt
 * nach der Hydration (siehe `THEME_OVERRIDES_COOKIE`). Deshalb nie nur das eine
 * schreiben — jede Schreibstelle geht hier durch.
 */
function merkeOverrides(next: ThemeOverrides): void {
  const json = JSON.stringify(next);
  try {
    localStorage.setItem(LS_OVERRIDES_KEY, json);
  } catch {
    /* Storage voll oder deaktiviert */
  }
  const leer =
    Object.keys(next.light ?? {}).length === 0 &&
    Object.keys(next.dark ?? {}).length === 0;
  // Leer heisst weg: ein Cookie mit `{"light":{},"dark":{}}` faehrt bei jeder
  // Anfrage mit und sagt nichts.
  if (leer || json.length > THEME_OVERRIDES_COOKIE_MAX) {
    deleteThemeCookie(THEME_OVERRIDES_COOKIE);
    return;
  }
  writeThemeCookie(THEME_OVERRIDES_COOKIE, json);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Die Theme-Schrift auf den Stand des Themes bringen — was hier nur noch heisst:
 * eine etwaige Inline-Ueberschreibung wegnehmen, damit die selbst ausgelieferte
 * Schrift aus der `<html>`-Klasse (Geist) wieder gilt.
 *
 * **Hier wird nichts mehr nachgeladen.** Bis 2026-08-10 haengte diese Funktion
 * aus `theme.fontUrl` ein `<link rel="stylesheet">` in den Kopf; beim Theme
 * "supabase" zeigte das auf `fonts.googleapis.com`, und der Browser des
 * angemeldeten Nutzers uebermittelte damit IP-Adresse, Browserkennung und
 * aufrufende Adresse an Google LLC in den USA — ohne Rechtsgrundlage nach
 * Kapitel V DSGVO. Das Feld `fontUrl` gibt es deshalb nicht mehr (s.
 * `lib/themes.ts`), und ein Theme, das nur `fontSans` fuehrt, setzt keine
 * Schrift: eine Familie, die niemand ausliefert, faellt sonst auf `system-ui`
 * statt auf Geist zurueck.
 */
function applyFont() {
  document.documentElement.style.removeProperty("--font-geist-sans");
}

function applyThemeVars(
  themeId: string,
  isDark: boolean,
  overrides: ThemeOverrides
) {
  const theme = getTheme(themeId);
  const vars = isDark ? theme.dark : theme.light;

  /**
   * EINGEBETTET GEHOEREN DIE FLAECHEN DEM WIRT — dann werden sie hier nicht
   * angefasst.
   *
   * Ohne das schreibt der Gast nach der Hydration seine eigenen Toene an
   * `<html>`, und die Wache in `useEmbeddedFlaecheBridge` holt sie sofort
   * wieder zurueck. Der Endzustand stimmt dadurch, aber der WEG ist sichtbar:
   * `globals.css` legt unter `data-theme-fx="soft"` eine 400-ms-Transition auf
   * `background-color`, mit `!important` und auf `*`. Jede Schreibung startet
   * also eine Ueberblendung zur Gastfarbe, die die Wache umkehrt — ein
   * Farbwackeln bei jedem Oeffnen des Panels.
   *
   * Uebersprungen werden NUR die Flaechen (`EMBED_FLAECHEN_VARIABLEN`).
   * `--primary` und die Handlungsfarben bleiben Sache des Gast-Themes; die
   * traegt die Farbthema-Bruecke, nicht diese.
   */
  const uebernommen = flaecheIstUebernommen(document.documentElement);
  const gehoertDemWirt = new Set(EMBED_FLAECHEN_VARIABLEN);

  for (const [key, value] of Object.entries(vars)) {
    if (uebernommen && gehoertDemWirt.has(`--${key}`)) continue;
    document.documentElement.style.setProperty(`--${key}`, value);
  }

  // Die Huellen-Flaechen NACH den Theme-Token und VOR den Anpassungen des
  // Nutzers: ein Theme darf seine Schale setzen (Janeway tut es), der Nutzer
  // darf sie ueberschreiben. Dieselbe Reihenfolge wie im SSR (`theme-ssr.ts`).
  const modeOverrides = overrides[isDark ? "dark" : "light"] ?? {};
  // Der Schalter kommt VOR der Leiter: er bestimmt, welche Leiter gerechnet
  // wird. Er ist keine Farbe und darf deshalb nicht als `--jw-flaechen` am
  // Element landen — er wird unten aus den Anpassungen uebersprungen.
  const schalter = modeOverrides["jw-flaechen"] === "eins" ? "eins" : undefined;
  // Als KLASSE, nicht als Attribut: CSS kann zwei Custom Properties nicht
  // vergleichen, braucht also einen Griff — und der Klassenname ist der
  // einzige, den der SSR schon heute durch alle 23 Apps reicht
  // (`theme-ssr.ts`, `htmlClassName`). Ein neues Rueckgabefeld muesste jedes
  // App-Layout einzeln mitnehmen; genau so entstehen die Stellen, die man
  // vergisst. Einflaechig fallen Rahmen und Inhalt farblich zusammen, und die
  // Umklammerung traegt dann eine Kante.
  document.documentElement.classList.toggle(
    "jw-eine-flaeche",
    (schalter ?? theme.huelle?.flaechen ?? "zwei") === "eins",
  );
  for (const [key, value] of Object.entries(huellenVars(theme, isDark, schalter))) {
    document.documentElement.style.setProperty(`--${key}`, value);
  }

  for (const [key, value] of Object.entries(modeOverrides)) {
    // `jw-flaechen` ist der Schalter, keine Farbe — er hat oben gewirkt.
    // Dasselbe gilt fuer die fuenf Huellen-Schalter: sie tragen alle das
    // Praefix `jw-huelle-`, damit hier EINE Regel reicht und nicht sechs
    // Namen stehen muessen. Wuerde einer durchrutschen, entstuende eine
    // Custom Property mit einem Wort als Wert — `--jw-schale: umklammert`
    // waere die Flaeche des Rahmens, und die Klammer waere weg.
    if (value == null || key === "jw-flaechen" || istHuellenSchluessel(key)) continue;
    // Und auch die Anpassung des Nutzers steht im Embed hinter dem Wirt
    // zurueck. Das ist kein Randfall: der Betreiber fuehrt `border` als
    // eigenen Wert (`hsl(0 0% 20%)`), und `--border` ist eine der Flaechen,
    // die die Bruecke aus der Wirtsflaeche ableitet. Ohne diese Zeile schreibt
    // seine Anpassung sie im Panel wieder zu.
    if (uebernommen && gehoertDemWirt.has(`--${key}`)) continue;
    document.documentElement.style.setProperty(`--${key}`, value);
  }

  applyFont();

  /* ── DIE STATUSLEISTE TRAEGT DEN RAHMEN, NICHT DAS THEME ──────────────────
     Die installierte PWA faerbt ihre Systemleiste nach `<meta
     name="theme-color">`. Ohne Mitschreiben kippt sie erst, wenn das System die
     Seite neu bewertet — sichtbar nach der Animation, waehrend die App schon
     neu aussieht.

     GESCHRIEBEN WIRD DIE FLAECHE, DIE OBEN AM BILDSCHIRM WIRKLICH STEHT. Bis
     0.473.0 stand hier `vars.background`, und das war unter „umklammert"
     falsch: dort malt die Huelle `--jw-grund` (im hellen Janeway-Theme
     rgb(0,0,0)), waehrend `--background` rgb(250,250,250) bleibt. Die Leiste
     stand also hell ueber einem schwarzen Rahmen. Derselbe Fehler wie beim
     Deckel der Wortmarke, nur eine Etage hoeher.

     Gelesen wird die GEMALTE Farbe der Huelle, nicht `--jw-grund` als Variable:
     unter „flach" ist sie ungesetzt und der Rueckfall steckt in der Benutzung.
     Gibt es keine Huelle (Anmeldeseiten, oeffentliche Seiten), bleibt es beim
     Theme-Hintergrund — dort ist er die Flaeche.

     Betreiber, 2026-09-20: „ich moechte die Notch nicht ausgrauen" — dafuer
     muss die Leiste die Farbe der App tragen, und zwar die richtige. */
  // ALLE `theme-color`-Zeilen, nicht die erste. `janewayViewport` setzt ZWEI
  // (`lib/pwa.ts`): eine fuer `prefers-color-scheme: light`, eine fuer `dark`.
  // `querySelector` traf nur die erste — im Dunkelmodus waehlt das System aber
  // die zweite, und die stand weiter auf dem festen `#000000` aus dem Viewport.
  // Wer ein Theme benutzt, dessen Rahmen NICHT schwarz ist, hatte dort also
  // eine Systemleiste, die nicht zur App passt. Betreiber, 2026-09-21: „Ich
  // wuerd's bevorzugen, wenn sie genau die gleiche Farbe hat wie die
  // Seitenleiste, weil die ist ja irgendwie auch dort mit umklammert."
  //
  // Beide zu setzen ist richtig und nicht bloss bequem: welche Zeile gilt,
  // entscheidet das SYSTEM-Farbschema, nicht unseres. Ein Nutzer mit hellem
  // System und dunklem App-Theme (oder umgekehrt) bekaeme sonst die Zeile, die
  // wir gerade nicht gepflegt haben.
  const metas = document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]');
  if (metas.length) {
    const huelle = document.querySelector("[data-jw-flaeche]");
    const gemalt = huelle ? getComputedStyle(huelle).backgroundColor : "";
    const ton = gemalt && !/^rgba\(0,\s*0,\s*0,\s*0\)$/.test(gemalt) ? gemalt : vars.background;
    if (ton) metas.forEach((m) => { m.content = ton; });
  }
}

/**
 * Die Variablen, die der Klon der Welle tragen muss — identisch zu dem, was
 * `applyThemeVars` am Ende tatsaechlich auf <html> schreibt.
 *
 * WARUM NICHT einfach `getTheme(id)[mode]`: das sind die ROHEN Theme-Werte.
 * `applyThemeVars` legt zusaetzlich die per-Variable `customOverrides` des
 * Nutzers darueber. Wer auch nur eine Farbe angepasst hat, saehe im Kreis sonst
 * die Theme-Vorgabe und erst am Ende einen harten Sprung auf seinen eigenen Wert.
 *
 * Die Schrift steht hier nicht mehr drin: seit kein Theme mehr eine eigene
 * mitbringt (s. `applyFont`), traegt der Klon dieselbe wie die Seite.
 */
function themeVarsFor(
  themeId: string,
  isDark: boolean,
  overrides: ThemeOverrides
): Record<string, string> {
  const theme = getTheme(themeId);
  const out: Record<string, string> = {
    ...(theme[isDark ? "dark" : "light"] as unknown as Record<string, string>),
  };
  const mode = overrides[isDark ? "dark" : "light"] ?? {};
  for (const [k, v] of Object.entries(mode)) if (v != null) out[k] = v;
  return out;
}

// ---------------------------------------------------------------------------
// Supabase persistence (optional — fails silently if not available)
// ---------------------------------------------------------------------------

interface SupabasePrefs {
  colorThemeId: string;
  overrides: ThemeOverrides;
  darkMode: boolean;
  /** Undefined, solange die Spalte auf einer aelteren DB fehlt. */
  themeTransition?: ThemeTransition;
  /** Undefined = nie gesetzt ODER Spalte fehlt (Migration 510). */
  voiceOrb?: VoiceOrbConfig;
}

async function loadFromSupabase(): Promise<SupabasePrefs | null> {
  try {
    const { createSupabaseBrowser } = await import("@janeway/app-sdk/supabase");
    const supabase = createSupabaseBrowser();
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return null;

    const { data } = await supabase
      .from("user_preferences")
      .select("color_theme_id, theme_overrides, dark_mode, theme_transition, voice_orb")
      .eq("user_id", session.user.id)
      .single();

    if (!data) return null;
    const fx = (data as { theme_transition?: unknown }).theme_transition;
    const orbRoh = (data as { voice_orb?: unknown }).voice_orb;
    return {
      colorThemeId: data.color_theme_id as string,
      overrides: (data.theme_overrides as ThemeOverrides) ?? { light: {}, dark: {} },
      darkMode: data.dark_mode as boolean,
      themeTransition: isThemeTransition(fx) ? fx : undefined,
      // NICHT gecastet, sondern geprueft: `user_preferences` ist eine Zeile, die
      // der Nutzer ueber PostgREST selbst schreiben kann, und der CHECK der
      // Migration laesst jedes JSON-Objekt durch. `leseVoiceOrb` verwirft
      // unbekannte Koerper, filtert unbekannte Animationen einzeln heraus und
      // klemmt Groesse und Bahnen in ihren Bereich — dieselbe Pruefung wie fuer
      // Cookie und localStorage, die genauso beschreibbar sind.
      voiceOrb: orbRoh ? (leseVoiceOrb(JSON.stringify(orbRoh)) ?? undefined) : undefined,
    };
  } catch {
    return null;
  }
}

/**
 * DIE EINE SCHLEUSE, DURCH DIE JEDE PERSISTENZ DIESES PROVIDERS LAEUFT.
 *
 * ── WARUM ES SIE GIBT ───────────────────────────────────────────────────────
 *
 * `localOnly` sperrte bis 0.439.0 nur Supabase. Die Theme-Cookies liefen
 * weiter — und die schreibt `writeThemeCookie` mit `Domain=.janewaylabs.com`,
 * also auf die ELTERN-Domain. Ein Klick im GUI-Showroom hat damit jede Labs-App
 * des Kontos umgefaerbt, ohne dass irgendwo Supabase im Spiel war.
 *
 * Am 2026-09-16 genau so passiert: der Betreiber probierte im Showroom den
 * Design-Reiter durch, und um 20:39 standen in seinem Konto `card`, `muted`,
 * `accent` und `secondary` auf `hsl(0 0% 0%)` — in JEDER Labs-App. Sein Befund:
 * „sehr viele Sachen sind auf einmal komplett schwarz". Der Showroom soll
 * ausprobieren lassen, nicht die Plattform verstellen.
 *
 * ── DIE SCHLEUSE STEHT SEIT 0.442.1 AN DER TUER, NICHT HIER ─────────────────
 *
 * Hier stand: „Es gibt vierzehn Schreibstellen im Provider … und die
 * fuenfzehnte, die jemand naechstes Jahr dazuschreibt, hat sie garantiert
 * nicht." Der Satz war richtig und bereits ueberholt: **fuenf Schreibstellen
 * ausserhalb dieser Datei gab es schon**, gemessen am 2026-09-17 —
 * `ThemeProvider.tsx:100` (Hell/Dunkel), `lib/view-mode.ts:33`,
 * `lib/theme-transition.ts:63` und zweimal im Onboarding. Keine davon lief
 * durch diese Schleuse.
 *
 * Der Waechter konnte es nicht sehen: er las ausschliesslich den Quelltext
 * DIESER Datei. Und `theme-nur-ueber-die-schleuse.test.ts`, auf den sich der
 * Satz berief, existierte im Baum ueberhaupt nicht — ein benannter Waechter,
 * den es nie gab.
 *
 * Deshalb sitzt die Pruefung jetzt in `lib/theme-cookie.ts`, wo der Keks
 * tatsaechlich geschrieben wird (`darfKeksSchreiben`). Der Provider meldet ihr
 * nur noch, ob diese Huelle abgeschottet ist. Wer kuenftig irgendwo eine
 * sechste Schreibstelle anlegt, kommt an ihr nicht vorbei —
 * `waechter-keks-nur-durch-die-tuer.test.ts` haelt fest, dass sie die einzige
 * Tuer bleibt.
 */

async function saveToSupabase(
  colorThemeId: string,
  overrides: ThemeOverrides,
  darkMode: boolean,
  themeTransition: ThemeTransition,
  voiceOrb: VoiceOrbConfig
) {
  try {
    const { createSupabaseBrowser } = await import("@janeway/app-sdk/supabase");
    const supabase = createSupabaseBrowser();
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    await supabase.from("user_preferences").upsert(
      {
        user_id: session.user.id,
        color_theme_id: colorThemeId,
        theme_overrides: overrides,
        dark_mode: darkMode,
        theme_transition: themeTransition,
        voice_orb: voiceOrb,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "user_id" }
    );
  } catch {
    // Supabase not available — localStorage is sufficient
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface ColorThemeProviderProps {
  children: ReactNode;
  /** Initialer Wert vom Server (Cookie). Verhindert Hydration-Flash. */
  initialColorThemeId?: string;
  /**
   * Symbol-Stil vom Server (`resolveInitialTheme(...).symbolStil`).
   *
   * **Jede App, die Dateisymbole zeigt, MUSS ihn setzen.** Ohne ihn startet der
   * Provider auf der Vorgabe und zieht erst nach der Hydration aus dem Cookie
   * nach — sichtbar als Formwechsel an jedem Symbol der Seite. Waechter:
   * `src/__tests__/symbolstil-im-layout.test.ts`.
   */
  initialSymbolStil?: SymbolStil;
  /**
   * Die Huelle schottet sich ab: **nichts verlaesst diesen Ursprung.**
   *
   * Kein Supabase (weder Lesen noch Schreiben), kein Realtime, und — seit
   * 0.439.0 — **auch keine Theme-Cookies**. Letzteres war die Luecke: Cookies
   * gehen auf die Eltern-Domain (`Domain=.janewaylabs.com`), ein Klick im
   * Showroom faerbte damit jede Labs-App des Kontos um, ganz ohne Supabase.
   * `localStorage` bleibt erlaubt — das ist je Ursprung getrennt und damit
   * genau das, was ein abgeschotteter Raum behalten darf.
   *
   * Vorgabe `false`: eine App speichert, das ist ihr Zweck. Setzen tut das
   * ausschliesslich ein Raum zum Ausprobieren (der GUI-Showroom).
   */
  localOnly?: boolean;
  /**
   * Die Huelle vom Server (`resolveInitialTheme(...).huelle`).
   *
   * **Jede App, die `AppLayout` rendert, MUSS sie setzen.** Ohne sie startet
   * die Huelle auf der Vorgabe der App und springt nach der Hydration auf die
   * des Kontos — Rinne, Radius und zwei Flaechen gleichzeitig, also die ganze
   * Klammer. Waechter: `src/__tests__/huelle-im-layout.test.ts`.
   */
  initialHuelle?: HuellenEinstellung | null;
}

export function ColorThemeProvider({
  children,
  initialColorThemeId,
  initialSymbolStil,
  initialHuelle,
  localOnly = false,
}: ColorThemeProviderProps) {
  // IM RENDERN, nicht in einem Effekt: die Effekte der Kinder laufen VOR denen
  // des Elternteils. Ein Kind, das beim Mounten schreibt, waere sonst schneller
  // als die Sperre — und genau ein solcher Klick hat den Schaden angerichtet.
  // Den fremden Rahmen prueft die Tuer selbst; sie kann das ueberall, diese
  // Zeile kennt nur den Teil, den nur sie kennt.
  setzeKeksAbschottung(localOnly);
  const { resolvedTheme, setTheme } = useTheme();
  const [colorThemeId, setColorThemeIdRoh] = useState(
    initialColorThemeId ?? "janeway"
  );

  /**
   * DAS FARBTHEMA DES WIRTS SCHLAEGT JEDE KONTOQUELLE — solange es eines gibt.
   *
   * ── WARUM EIN TOR UND NICHT SIEBEN ABFRAGEN ─────────────────────────────
   *
   * Das angezeigte Farbthema wird an SIEBEN Stellen gesetzt: beim Mounten aus
   * `localStorage`, aus den Kontoeinstellungen, ueber den Realtime-Kanal, beim
   * Fokus aus dem Keks, ueber das `storage`-Ereignis anderer Tabs, durch die
   * Wahl des Nutzers — und jetzt durch den Wirt. Eine Abfrage an jeder waere
   * siebenmal die Gelegenheit, eine zu vergessen.
   *
   * Genau diesen Fehler habe ich heute schon einmal behoben (die Keks-Schleuse
   * stand am Aufrufer statt an der Tuer, und fuenf Schreibstellen liefen
   * daran vorbei). Deshalb hier gleich das Tor: der ROHE Setzer ist privat,
   * jeder Weg geht durch diesen einen.
   *
   * ── WARUM DER NUTZER TROTZDEM GEWINNT ───────────────────────────────────
   *
   * `setColorTheme` — die Wahl im Design-Reiter — loescht die Marke, bevor es
   * setzt. Sonst wuerde ein Klick in einer eingebetteten App still verschluckt,
   * und das waere dieselbe Klasse Fehler wie die zu breite Speichersperre, die
   * heute schon einmal zwei Positivkontrollen gerissen hat.
   */
  const wirtFarbthema = useRef<string | null>(null);
  const setColorThemeIdState = useCallback((id: string) => {
    if (wirtFarbthema.current !== null && id !== wirtFarbthema.current) return;
    setColorThemeIdRoh(id);
  }, []);
  const [customOverrides, setCustomOverrides] = useState<ThemeOverrides>({
    light: {},
    dark: {},
  });
  /**
   * DIE SAAT VOM SERVER — sie gilt, bis die Anpassungen im Browser stehen.
   *
   * Die Wahl liegt in `customOverrides`, und die sind beim ersten Bild leer
   * (der Mount-Effekt liest Cookie und `localStorage` erst danach). Ohne die
   * Saat rendert der Server also die Vorgabe der App und der Browser eine
   * Hundertstelsekunde spaeter die des Kontos: Rinne, Radius und zwei Flaechen
   * auf einmal.
   *
   * ALS ZUSTAND UND NICHT ALS KONSTANTE, damit `resetOverrides` sie mitnehmen
   * kann: nach „alles zuruecksetzen" faellt die Huelle auf die Vorgabe der App,
   * und eine unveraenderliche Saat wuerde die alte Wahl bis zum Neuladen
   * weiterbehaupten.
   */
  const [saatHuelle, setSaatHuelle] = useState<HuellenEinstellung | null>(
    initialHuelle ?? null,
  );
  const [logoHoverAnim, setLogoHoverAnimState] = useState(true);
  const [symbolStil, setSymbolStilState] = useState<SymbolStil>(
    initialSymbolStil ?? SYMBOL_STIL_VORGABE,
  );
  /**
   * Kam der Stil vom Server, ist er die Wahrheit — dann darf der Mount-Effekt
   * den Cookie NICHT noch einmal lesen und anwenden. Er tut es sonst mit
   * demselben Wert, und im Fehlerfall (App setzt `initialSymbolStil` nicht)
   * wuerde der Nachzug den Flash erst sichtbar machen, statt ihn zu heilen.
   */
  const stilKamVomServer = useRef(initialSymbolStil !== undefined);
  const [voiceOrb, setVoiceOrbState] = useState<VoiceOrbConfig>(VOICE_ORB_DEFAULT);
  const [mounted, setMounted] = useState(false);
  const [pendingMode, setPendingMode] = useState<ThemeMode | null>(null);
  /**
   * Der Modus, wie er GERADE AUF DEM DOKUMENT liegt — umgeschaltet exakt im
   * `apply()`-Callback der Uebergangsanimation, nicht davor und nicht danach.
   *
   * WOZU, wenn es `pendingMode` schon gibt: `pendingMode` kippt absichtlich VOR
   * dem Start der Animation (flushSync, damit der Mobil-Klon das neue Symbol
   * traegt). Alles, was daraus seine Farbe zieht, steht damit schon im ALTEN
   * Schnappschuss der View-Transition neu da und nimmt an der Animation nicht
   * teil — im Design-Panel sprangen dadurch die 43 Farbkacheln um, waehrend der
   * Rest der Seite noch sauber durchwischte.
   *
   * `apply()` ist die Schnappschuss-Grenze: davor = alt, danach = neu. Wer hier
   * mitkippt, animiert korrekt mit.
   */
  const [appliedDark, setAppliedDark] = useState<boolean | null>(null);
  const [themeTransition, setThemeTransitionState] =
    useState<ThemeTransition>(DEFAULT_THEME_TRANSITION);
  const [designPanelOpen, setDesignPanelOpen] = useState(false);
  const [designPanelHosts, setDesignPanelHosts] = useState(0);
  const registerDesignPanelHost = useCallback(() => {
    setDesignPanelHosts((c) => c + 1);
    return () => setDesignPanelHosts((c) => c - 1);
  }, []);
  // Zusätzlich als Ref, weil die Setter-Callbacks sonst den Wert vom Zeitpunkt
  // ihrer Erzeugung sähen und eine gerade geänderte Einstellung ignorierten.
  const transitionRef = useRef<ThemeTransition>(DEFAULT_THEME_TRANSITION);

  const saveRef = useRef({
    colorThemeId: "janeway",
    overrides: { light: {}, dark: {} } as ThemeOverrides,
    darkMode: true,
    themeTransition: DEFAULT_THEME_TRANSITION as ThemeTransition,
    voiceOrb: VOICE_ORB_DEFAULT as VoiceOrbConfig,
    timer: null as ReturnType<typeof setTimeout> | null,
  });

  /**
   * Der zuletzt geaeusserte Wunsch, waehrend noch eine Uebergangsanimation lief.
   *
   * WARUM gemerkt statt verworfen: Im Design-Schnellpanel liegen Modus, Effekt und
   * ~43 Farbkacheln nebeneinander und werden schnell durchprobiert. Eine Welle
   * dauert bis 680 ms — jeder Klick in diesem Fenster ging vorher ersatzlos
   * verloren, ohne Rueckmeldung.
   *
   * WARUM nicht die laufende Welle abbrechen: `runThemeTransition` beendet eine
   * laufende Welle bei jedem neuen Aufruf (`activeWave()`), und genau dieser
   * Abbruch war der frueher behobene "harte Sprung" beim Doppelklick. Deshalb
   * laeuft die aktuelle Animation immer sauber zu Ende und der gemerkte Wunsch
   * startet erst danach.
   */
  const pendingChangeRef = useRef<(() => void) | null>(null);

  /** Laeuft am Ende JEDER Transition (als deren `onSettled`). */
  const flushPendingChange = useCallback(() => {
    const next = pendingChangeRef.current;
    pendingChangeRef.current = null;
    next?.();
  }, []);

  const scheduleSave = useCallback(() => {
    /* HIER STEHT BEWUSST **KEIN** `imFremdenRahmen()`, obwohl die Kekse es
       haben — und der Unterschied ist der ganze Punkt.

       Die Rueckkopplung, die der Betreiber gemeldet hat, laeuft ueber den KEKS:
       die eingebettete App holt beim Laden das Konto-Theme und schreibt
       `COLOR_THEME_COOKIE` auf die Eltern-Domain; der Wirt liest ihn bei
       `focus` zurueck. Diesen Weg schliesst die Tuer in `lib/theme-cookie.ts`. Der Weg in die
       DATENBANK ist an der Rueckkopplung nicht beteiligt: er wird beim Laden
       gar nicht erst betreten (`loadFromSupabase` setzt `saveRef` direkt), und
       was ueber die Bruecke hereinkommt, faengt `verbraucheThemeVomWirt` ab.

       Ich hatte die Sperre am 2026-09-17 trotzdem hierher kopiert — aus
       Symmetrie, nicht aus Messung. `embed-bruecke-persistiert-nicht.test.tsx`
       hat sie mit zwei Faellen widerlegt: eine ECHTE Wahl des Nutzers in einer
       eingebetteten App kam danach nirgends mehr an. Der Test nennt den Grund
       im Kopf — „ohne sie waere ‚speichert nie' eine bestandene Pruefung mit
       kaputtem Produkt". Genau das war es. */
    if (localOnly) return;
    if (saveRef.current.timer) clearTimeout(saveRef.current.timer);
    saveRef.current.timer = setTimeout(() => {
      saveToSupabase(
        saveRef.current.colorThemeId,
        saveRef.current.overrides,
        saveRef.current.darkMode,
        saveRef.current.themeTransition,
        saveRef.current.voiceOrb
      );
    }, 800);
  }, [localOnly]);

  // Initialize from Cookie (cross-subdomain) → localStorage → Supabase
  useEffect(() => {
    // Cookie hat Prioritaet: zeigt cross-subdomain immer den echten Wert.
    const cookieTheme = readThemeCookie(COLOR_THEME_COOKIE);
    const savedTheme = cookieTheme ?? localStorage.getItem(LS_THEME_KEY);
    if (savedTheme) {
      setColorThemeIdState(savedTheme);
      saveRef.current.colorThemeId = savedTheme;
      if (cookieTheme && cookieTheme !== localStorage.getItem(LS_THEME_KEY)) {
        localStorage.setItem(LS_THEME_KEY, cookieTheme);
      }
    }

    // Cookie zuerst — dieselbe Rangfolge wie beim Farbschema: er gilt
    // cross-subdomain und ist derselbe Wert, mit dem der Server gerendert hat.
    // Der localStorage kann in einer anderen App aelter sein.
    const cookieOverrides = readThemeCookie(THEME_OVERRIDES_COOKIE);
    const lokaleOverrides = localStorage.getItem(LS_OVERRIDES_KEY);
    const savedOverrides = cookieOverrides ?? lokaleOverrides;
    if (savedOverrides) {
      try {
        const parsed = JSON.parse(savedOverrides) as ThemeOverrides;
        setCustomOverrides(parsed);
        saveRef.current.overrides = parsed;
        if (cookieOverrides) {
          if (cookieOverrides !== lokaleOverrides) {
            localStorage.setItem(LS_OVERRIDES_KEY, cookieOverrides);
          }
        } else {
          // Nachruesten fuer alle, die ihre Anpassungen vor 0.279.2 gesetzt
          // haben: die liegen im localStorage, aber noch nicht als Cookie —
          // ohne diesen Zweig faerbte es bei ihnen bis zum naechsten Bearbeiten
          // weiter um, und das kann Jahre dauern.
          merkeOverrides(parsed);
        }
      } catch {
        /* corrupt data — ignore */
      }
    }

    const cookieOrb = readThemeCookie(VOICE_ORB_COOKIE);
    const savedOrb = cookieOrb ?? localStorage.getItem(LS_VOICE_ORB_KEY);
    // Als Boolean festhalten, NICHT spaeter erneut lesen: derselbe Grund wie bei
    // `hadStoredTransition`. Dieser Zweig seedet zwar nichts, aber ein zweiter
    // Read waere trotzdem die falsche Frage — er beantwortet „gibt es jetzt
    // einen Cookie", gebraucht wird „gab es beim Ankommen einen".
    const hatteOrb = savedOrb != null;
    if (savedOrb) {
      const geprueft = leseVoiceOrb(savedOrb);
      if (geprueft) {
        setVoiceOrbState(geprueft);
        saveRef.current.voiceOrb = geprueft;
        if (cookieOrb && cookieOrb !== localStorage.getItem(LS_VOICE_ORB_KEY)) {
          localStorage.setItem(LS_VOICE_ORB_KEY, cookieOrb);
        }
      }
    }

    const cookieLogoHover = readThemeCookie(LOGO_HOVER_COOKIE);
    const savedLogoHover = cookieLogoHover ?? localStorage.getItem(LS_LOGO_HOVER_KEY);
    if (savedLogoHover != null) {
      setLogoHoverAnimState(savedLogoHover !== "0");
      if (cookieLogoHover && cookieLogoHover !== localStorage.getItem(LS_LOGO_HOVER_KEY)) {
        localStorage.setItem(LS_LOGO_HOVER_KEY, cookieLogoHover);
      }
    }

    // Nur nachlesen, wenn das Layout den Stil NICHT mitgegeben hat (dann gilt
    // die Vorgabe, und ein gesetzter Cookie soll wenigstens verspaetet greifen).
    // Kam er vom Server, steht er schon im ersten Markup.
    if (!stilKamVomServer.current) {
      const cookieStil = readThemeCookie(SYMBOL_STIL_COOKIE);
      if (istSymbolStil(cookieStil)) setSymbolStilState(cookieStil);
    }

    // Cross-App geteilt (Cookie > localStorage), genau wie Farb-Theme und
    // Logo-Hover. Der Effekt wird nur im Moment der Interaktion gelesen, nie beim
    // ersten Paint — deshalb ist das trotz Cookie FOUC-frei.
    // Vorhandensein MUSS vor dem Seed festgehalten werden: schriebe der Seed
    // unten unbedingt, gaebe es ab hier IMMER einen Cookie, und der DB-Zweig in
    // loadFromSupabase().then() (der genau "kein Cookie auf diesem Geraet"
    // prueft) waere fuer immer unerreichbar — ein per Realtime einlaufender
    // DB-Wert eines anderen Geraets ginge dann nie mehr durch.
    const hadStoredTransition =
      isThemeTransition(readThemeCookie(THEME_TRANSITION_COOKIE)) ||
      isThemeTransition(localStorage.getItem(THEME_TRANSITION_LS_KEY));
    const fx = readThemeTransition();
    setThemeTransitionState(fx);
    transitionRef.current = fx;
    saveRef.current.themeTransition = fx;
    // Nur heilen, was schon da war (abweichender localStorage bei bestehendem
    // Cookie) — NICHT den Default fuer Erstbesucher saeen. Sonst existiert der
    // Cookie ab hier immer und der DB-Zweig unten wird nie erreicht.
    if (hadStoredTransition) writeThemeTransition(fx);
    trackPointerOrigin();

    setMounted(true);

    if (!localOnly) {
      loadFromSupabase().then((prefs) => {
        if (!prefs) return;
        setColorThemeIdState(prefs.colorThemeId);
        setCustomOverrides(prefs.overrides);

        // Effekt: nur uebernehmen, wenn dieses Geraet noch KEINEN geteilten Cookie
        // hatte. Sonst gilt — wie bei dark_mode — der Cookie als Geraete-Wahrheit und
        // die DB nur als Cross-Device-Fallback. Ohne diese Regel ueberschriebe eine
        // veraltete DB-Zeile die gerade in einer anderen App getroffene Wahl.
        // Bewusst der VOR dem Seed erfasste Zustand (`hadStoredTransition`), nicht
        // erneut readThemeCookie(): der Mount-Effekt oben hat den Cookie fuer
        // Erstbesucher inzwischen synchron gesetzt, ein erneuter Read waere hier
        // immer "vorhanden" und der Zweig unerreichbar.
        if (prefs.themeTransition && !hadStoredTransition) {
          setThemeTransitionState(prefs.themeTransition);
          transitionRef.current = prefs.themeTransition;
          saveRef.current.themeTransition = prefs.themeTransition;
          writeThemeTransition(prefs.themeTransition);
        }

        // Sprachvisualisierung: dieselbe Regel. Der Cookie ist die Wahrheit des
        // GERAETS, die DB der Cross-Device-Fallback fuer ein Geraet, das noch
        // keinen hat. Umgekehrt uebersteuerte eine alte Zeile die Wahl, die
        // gerade in einer anderen App getroffen wurde.
        if (prefs.voiceOrb && !hatteOrb) {
          setVoiceOrbState(prefs.voiceOrb);
          saveRef.current.voiceOrb = prefs.voiceOrb;
          const roh = JSON.stringify(prefs.voiceOrb);
          localStorage.setItem(LS_VOICE_ORB_KEY, roh);
          writeThemeCookie(VOICE_ORB_COOKIE, roh);
        }

        // Light/Dark NICHT bedingungslos aus der DB erzwingen: `dark_mode` hat
        // `DEFAULT true` und wuerde eine per SSR korrekt hell gerenderte Seite nach
        // dem Paint auf dunkel kippen — und das Dunkel ueber den Cookie-Writer in
        // alle Apps tragen. Der Cookie ist die Geraete-Wahrheit; die DB nur der
        // Cross-Device-Fallback, wenn es auf diesem Geraet noch keinen Cookie gibt.
        const cookieMode = readThemeCookie(THEME_COOKIE);
        if (shouldAdoptRemoteDarkMode(cookieMode, prefs.darkMode)) {
          setTheme(prefs.darkMode ? "dark" : "light");
          saveRef.current.darkMode = prefs.darkMode;
        } else {
          // Cookie gewinnt — und heilt eine veraltete DB-Zeile (einmalig, mit dem
          // lokal gewaehlten Wert), statt sich von ihr ueberschreiben zu lassen.
          const localDark = cookieMode === "dark";
          saveRef.current.darkMode = localDark;
          if (prefs.darkMode !== localDark) scheduleSave();
        }

        saveRef.current.colorThemeId = prefs.colorThemeId;
        saveRef.current.overrides = prefs.overrides;
        localStorage.setItem(LS_THEME_KEY, prefs.colorThemeId);
        merkeOverrides(prefs.overrides);
        writeThemeCookie(COLOR_THEME_COOKIE, prefs.colorThemeId);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Realtime sync across tabs/apps via Supabase
  useEffect(() => {
    if (!mounted || localOnly) return;

    let cleanup: (() => void) | undefined;
    let cancelled = false;

    (async () => {
      try {
        const { createSupabaseBrowser, authorizeRealtime } = await import("@janeway/app-sdk/supabase");
        const sb = createSupabaseBrowser();
        // Ohne setAuth laeuft der Socket als `anon`, und die RLS-Policy auf
        // `user_preferences` (TO authenticated) verwirft jede Zeile still —
        // siehe authorizeRealtime im app-sdk. Der Aufraeumer beendet den
        // onAuthStateChange-Listener, der den Token nach jedem Refresh
        // nachzieht; ohne ihn faellt der Socket stuendlich stumm auf `anon`.
        const releaseAuth = await authorizeRealtime(sb);
        if (cancelled) {
          releaseAuth();
          return;
        }

        const channel = sb
          .channel("theme-sync")
          .on(
            "postgres_changes",
            { event: "UPDATE", schema: "public", table: "user_preferences" },
            (payload: { new: Record<string, unknown> }) => {
              const d = payload.new as {
                color_theme_id?: string;
                theme_overrides?: ThemeOverrides;
                dark_mode?: boolean;
                theme_transition?: unknown;
                voice_orb?: unknown;
              };

              // Pro Achse anwenden, wenn sie vom zuletzt bekannten Wert (saveRef)
              // abweicht. Das macht Echos unserer eigenen Saves automatisch zu No-Ops.
              if (d.color_theme_id && d.color_theme_id !== saveRef.current.colorThemeId) {
                setColorThemeIdState(d.color_theme_id);
                saveRef.current.colorThemeId = d.color_theme_id;
                localStorage.setItem(LS_THEME_KEY, d.color_theme_id);
                writeThemeCookie(COLOR_THEME_COOKIE, d.color_theme_id);
              }
              if (d.theme_overrides) {
                setCustomOverrides(d.theme_overrides);
                saveRef.current.overrides = d.theme_overrides;
                merkeOverrides(d.theme_overrides);
              }
              if (d.dark_mode !== undefined && d.dark_mode !== saveRef.current.darkMode) {
                setTheme(d.dark_mode ? "dark" : "light");
                saveRef.current.darkMode = d.dark_mode;
              }
              if (
                isThemeTransition(d.theme_transition) &&
                d.theme_transition !== saveRef.current.themeTransition
              ) {
                setThemeTransitionState(d.theme_transition);
                transitionRef.current = d.theme_transition;
                saveRef.current.themeTransition = d.theme_transition;
                writeThemeTransition(d.theme_transition);
              }
              if (d.voice_orb) {
                // Ueber den Rohtext vergleichen statt Feld fuer Feld: `fx` ist ein
                // Array, und ein Vergleich mit !== waere hier immer wahr — die
                // Figur wuerde bei JEDEM fremden Save neu gesetzt.
                const roh = JSON.stringify(d.voice_orb);
                if (roh !== JSON.stringify(saveRef.current.voiceOrb)) {
                  const geprueft = leseVoiceOrb(roh);
                  if (geprueft) {
                    setVoiceOrbState(geprueft);
                    saveRef.current.voiceOrb = geprueft;
                    const rein = JSON.stringify(geprueft);
                    localStorage.setItem(LS_VOICE_ORB_KEY, rein);
                    writeThemeCookie(VOICE_ORB_COOKIE, rein);
                  }
                }
              }
            }
          )
          .subscribe();

        cleanup = () => {
          releaseAuth();
          sb.removeChannel(channel);
        };
      } catch {
        // Supabase not available
      }
    })();

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [mounted, localOnly, setTheme]);

  // Apply CSS variables when theme changes
  useEffect(() => {
    if (!mounted) return;
    applyThemeVars(colorThemeId, resolvedTheme === "dark", customOverrides);
  }, [colorThemeId, resolvedTheme, customOverrides, mounted]);

  // Persist dark mode preference — nur bei echter Aenderung, nie beim ersten
  // Beobachten. Sonst schreibt schon das blosse Oeffnen einer App den aktuell
  // gerenderten Wert als "Nutzerpraeferenz" in die DB; in Cookie-losen Kontexten
  // (Share-Seite, iframe, frisches Geraet) ist das der Dark-Default.
  const firstDarkObservation = useRef(true);
  useEffect(() => {
    if (!mounted) return;
    if (resolvedTheme !== "light" && resolvedTheme !== "dark") return;
    const isDark = resolvedTheme === "dark";
    if (firstDarkObservation.current) {
      firstDarkObservation.current = false;
      saveRef.current.darkMode = isDark;
      return;
    }
    /* ANGEWENDET, NICHT GESPEICHERT.
       Ein Theme, das ueber die Einbettungs-Bruecke hereinkam, ist eine
       DARSTELLUNGSANWEISUNG DES WIRTS und keine Entscheidung des Nutzers
       (`lib/embed-theme.ts`). Der Provider kann die Herkunft nicht sehen — der
       Kommentar 20 Zeilen weiter unten fuehrt die iframe-Bruecke laengst als
       einen der Wege auf, die an `setMode` vorbeilaufen; genau das war das Leck.

       Gemessen am 2026-09-16: EINE Theme-Nachricht des Wirts erzeugte EINEN
       `upsert` auf `user_preferences`. Im GUI-Showroom, der echte Apps
       einbettet, schrieb damit jeder Klick in die Kontoeinstellungen des
       Betreibers — in jede Labs-App, per Realtime auch in offene Tabs.

       DIE REIHENFOLGE IST TRAGEND: die Abfrage steht VOR dem
       `saveRef`-Vergleich. Stuende sie danach, hielte `saveRef` den Wert des
       Wirts, und der naechste Speichervorgang aus ganz anderem Anlass
       (Farbthema, Orb, Uebergang) truege ihn durch die Hintertuer doch ins
       Konto. */
    if (verbraucheThemeVomWirt(isDark ? "dark" : "light")) return;
    if (saveRef.current.darkMode === isDark) return;
    saveRef.current.darkMode = isDark;
    scheduleSave();
  }, [resolvedTheme, mounted, scheduleSave]);

  // Sobald next-themes nachgezogen hat, ist der Zwischenzustand ueberfluessig.
  useEffect(() => {
    if (pendingMode && resolvedTheme === pendingMode) setPendingMode(null);
  }, [pendingMode, resolvedTheme]);

  // Wechsel, die NICHT ueber setMode laufen (Cross-Tab-Sync, Profil-Sync,
  // System-Dark-Mode, iframe-Bridge), gehen an `apply()` vorbei. Ohne dieses
  // Nachziehen bliebe `appliedDark` dort auf dem alten Wert stehen.
  useEffect(() => {
    if (resolvedTheme !== "dark" && resolvedTheme !== "light") return;
    setAppliedDark(resolvedTheme === "dark");
  }, [resolvedTheme]);

  // Logo-Hover-Animation als DOM-Attribut spiegeln — die Logo-Styles reagieren
  // global darauf (CSS in globals.css), ohne Prop-Drilling durch die Apps.
  useEffect(() => {
    if (!mounted) return;
    document.documentElement.dataset.logoHoverAnim = logoHoverAnim ? "on" : "off";
  }, [logoHoverAnim, mounted]);

  /**
   * DAS FARBTHEMA DES WIRTS — die Achse, die bis 0.442.2 gar nicht mitfuhr.
   *
   * Gemessen am 2026-09-17 im laufenden Showroom (Wirt „Claude"/dunkel, alle
   * Inline-Token an beiden Wurzelknoten verglichen): 28 von 29 Token weichen
   * ab, der Modus stimmt. 15 deckt die Flaechen-Bruecke, **zwoelf bleiben** —
   * `--primary`, `--ring`, das Sekundaer- und das Destruktivpaar, dazu die
   * Seitenleisten-Entsprechungen. Der Wirt stand auf warmem `hsl(18 57% 46%)`,
   * das Kind auf `hsl(0 0% 98%)`.
   *
   * Zwei Wege fuer einen Wert, dieselbe Begruendung wie bei der Flaeche:
   *   1. `?farbthema=<id>` an der Adresse — reist mit der ANFRAGE.
   *   2. `postMessage` — fuer jede spaetere Aenderung.
   *
   * ── ANGEWENDET, NICHT GESPEICHERT — und hier ohne eigene Marke ───────────
   *
   * Der Modus braucht `verbraucheThemeVomWirt`, weil `setTheme` durch
   * next-themes laeuft und der Persistenz-Effekt die Herkunft nicht mehr sieht.
   * Beim Farbthema ist das nicht noetig: der Effekt, der die Variablen
   * schreibt, haengt allein am Zustand. Wer nur den Zustand setzt, faerbt —
   * und beruehrt weder `localStorage` noch Keks noch `saveRef`. Die
   * persistierenden Zeilen stehen ausschliesslich in `setColorTheme`.
   *
   * **Das ist kleiner, als ich es angekuendigt hatte.** Ich wollte einen
   * Schalter `nurAnwenden` an `setColorTheme` bauen; beim Lesen war er
   * ueberfluessig. Ein Schalter, den niemand braucht, ist ein zweiter Eingang
   * fuer dieselbe Zusage.
   */
  useEffect(() => {
    if (!mounted) return;
    if (typeof window === "undefined" || window.self === window.top) return;

    const uebernimm = (id: string) => {
      if (!istFarbthemaId(id) || id === wirtFarbthema.current) return;
      wirtFarbthema.current = id;
      setColorThemeIdState(id);
    };

    // 1. Der Weg fuers erste Bild — hier schon da, aber erst nach der
    //    Hydration gelesen. Das Serverbild traegt weiter das Konto-Thema;
    //    siehe den Kopf von `embed-theme.ts`, warum das so bleibt.
    const ausUrl = farbthemaAusUrl(window.location.search);
    if (ausUrl) uebernimm(ausUrl);

    // 2. Der Weg fuer jede spaetere Aenderung.
    const onMessage = (event: MessageEvent) => {
      if (!isAllowedEmbedOrigin(event.origin, EMBED_PARENT_ORIGIN_SUFFIXES)) return;
      if (!isEmbedFarbthemaMessage(event.data)) return;
      uebernimm(event.data.__janewayEmbedFarbthema);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [mounted, setColorThemeIdState]);

  // B1 — Cross-App-Sync bei Tab-Refokus: geteilte Theme-Cookies neu lesen und
  // nur geaenderte Achsen anwenden. Cookies sind hostname-scoped -> wirkt
  // zwischen Apps auf verschiedenen Ports/Subdomains, ohne Login/DB-Roundtrip.
  useEffect(() => {
    if (!mounted) return;
    const sync = () => {
      if (document.visibilityState === "hidden") return;
      /* WER NICHT SCHREIBEN DARF, DARF AUCH NICHT NACHZIEHEN.
         Ohne diese Zeile war „das Theme springt zurueck" kein Fehlerfall,
         sondern zwangslaeufig: die Tuer verbietet einer abgeschotteten Huelle
         und jedem Dokument im fremden Rahmen das SCHREIBEN des Keks — hier
         wurde er weiter GELESEN, und `pickCookieThemeUpdate` gibt jedem
         abweichenden Wert den Vorrang. Am 2026-09-17 im Showroom gemessen,
         und zwar deterministisch bei JEDEM Fokus, nicht „manchmal":

           Start (Keks=janeway)   --primary hsl(0 0% 9%)
           Klick auf „Mono"       --primary hsl(0 0% 6%)   Keks unveraendert
           nach focus             --primary hsl(0 0% 9%)   ← zurueckgesprungen

         Fuer eine eingebettete App gilt dasselbe, denn dort ist der fremde
         Rahmen immer wahr: der Betreiber klickt ins Panel, das Panel bekommt
         den Fokus, und das Theme des Wirts wird vom Konto-Keks ueberschrieben.
         Die Bruecke wendet bewusst nur an und speichert nicht — dieser
         Abgleich machte genau das im naechsten Fokus-Ereignis rueckgaengig. */
      if (!darfKeksSchreiben()) return;
      const update = pickCookieThemeUpdate(
        { mode: (resolvedTheme === "dark" ? "dark" : "light") as ThemeMode, colorThemeId },
        { mode: readThemeCookie(THEME_COOKIE), colorThemeId: readThemeCookie(COLOR_THEME_COOKIE) }
      );
      if (update.mode) setTheme(update.mode);
      if (update.colorThemeId) {
        setColorThemeIdState(update.colorThemeId);
        saveRef.current.colorThemeId = update.colorThemeId;
        localStorage.setItem(LS_THEME_KEY, update.colorThemeId);
      }
      const cookieLogo = readThemeCookie(LOGO_HOVER_COOKIE);
      if (cookieLogo != null) {
        const on = cookieLogo !== "0";
        if (on !== logoHoverAnim) {
          setLogoHoverAnimState(on);
          localStorage.setItem(LS_LOGO_HOVER_KEY, cookieLogo);
        }
      }
      // Uebergangs-Effekt: greift auch ohne Login, wo es keinen Realtime-Kanal gibt.
      const cookieFx = readThemeCookie(THEME_TRANSITION_COOKIE);
      if (isThemeTransition(cookieFx) && cookieFx !== transitionRef.current) {
        setThemeTransitionState(cookieFx);
        transitionRef.current = cookieFx;
        saveRef.current.themeTransition = cookieFx;
        localStorage.setItem(THEME_TRANSITION_LS_KEY, cookieFx);
      }
    };
    document.addEventListener("visibilitychange", sync);
    window.addEventListener("focus", sync);
    return () => {
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("focus", sync);
    };
  }, [mounted, resolvedTheme, colorThemeId, logoHoverAnim, setTheme]);

  // B2 — Same-Origin-Sync (mehrere Tabs derselben App): storage-Event spiegelt,
  // was next-themes fuer Light/Dark schon tut, auf Color-Theme/Overrides/Logo.
  // Feuert nur in ANDEREN Tabs -> kein Self-Echo, keine Endlosschleife.
  useEffect(() => {
    if (!mounted) return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === LS_THEME_KEY && e.newValue) {
        setColorThemeIdState(e.newValue);
        saveRef.current.colorThemeId = e.newValue;
      } else if (e.key === LS_OVERRIDES_KEY) {
        try {
          const next = e.newValue
            ? (JSON.parse(e.newValue) as ThemeOverrides)
            : { light: {}, dark: {} };
          setCustomOverrides(next);
          saveRef.current.overrides = next;
        } catch {
          /* corrupt data — ignore */
        }
      } else if (e.key === LS_LOGO_HOVER_KEY && e.newValue != null) {
        setLogoHoverAnimState(e.newValue !== "0");
      } else if (e.key === THEME_TRANSITION_LS_KEY && isThemeTransition(e.newValue)) {
        setThemeTransitionState(e.newValue);
        transitionRef.current = e.newValue;
        saveRef.current.themeTransition = e.newValue;
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [mounted]);

  const setColorTheme = useCallback(
    (id: string, originEl?: Element | null) => {
      // Der Nutzer schlaegt den Wirt. Ohne diese Zeile verschluckt das Tor
      // oben seinen Klick in einer eingebetteten App — lautlos.
      wirtFarbthema.current = null;
      const run = () => {
        runThemeTransition(transitionRef.current, () => {
          // Variablen SYNCHRON schreiben. Der Effekt weiter oben tut dasselbe,
          // aber erst nach dem Paint — startViewTransition schnappt den DOM
          // jedoch sofort im Callback. Ohne diesen Direktschreiber zeigte die
          // Transition zweimal denselben Zustand. Idempotent, der Effekt darf
          // hinterher gefahrlos dieselben Werte nochmal setzen.
          applyThemeVars(id, resolvedTheme === "dark", customOverrides);
          setColorThemeIdState(id);
        }, originEl,
          themeVarsFor(id, resolvedTheme === "dark", customOverrides),
          resolvedTheme === "dark",
          flushPendingChange);
        localStorage.setItem(LS_THEME_KEY, id);
        writeThemeCookie(COLOR_THEME_COOKIE, id);
        saveRef.current.colorThemeId = id;
        scheduleSave();
      };
      // Waehrend einer laufenden Welle NICHT erneut umschalten. Zwei Wellen
      // koennen nicht koexistieren, und eine laufende kann ihr Ziel nicht
      // wechseln — der Klon im Kreis traegt bereits die Farben des ersten
      // Ziels. Frueher brach ein zweiter Klick die erste Welle ab; das setzte
      // sie ohne Animation auf ihr Ziel, und genau dieser harte Sprung war der
      // sichtbare Fehler beim schnellen Doppelklick. Statt den Klick zu
      // verwerfen, wird er gemerkt und laeuft automatisch (via `onSettled`
      // oben), sobald die laufende Welle fertig ist — "latest wins", der
      // zuletzt geklickte Wunsch gewinnt.
      if (isThemeTransitionBusy()) {
        pendingChangeRef.current = run;
        return;
      }
      run();
    },
    [scheduleSave, resolvedTheme, customOverrides, flushPendingChange]
  );

  const setMode = useCallback(
    (mode: ThemeMode, originEl?: Element | null) => {
      // Bereits im Zielmodus: nichts zu tun. Sonst liefe eine volle Welle ohne
      // jede sichtbare Aenderung und blockierte waehrenddessen die uebrigen
      // Bedienelemente.
      const current = pendingMode ?? (resolvedTheme === "dark" ? "dark" : "light");
      if (mode === current) return;

      const run = () => {
        // SYNCHRON vor dem Klonen: Die Welle kopiert den DOM sofort. Wuerde React
        // das Icon erst im naechsten Render tauschen, traege der Klon — und damit
        // der ganze Kreis — noch das alte Symbol.
        flushSync(() => setPendingMode(mode));
        runThemeTransition(transitionRef.current, () => {
          // Auch hier synchron: next-themes setzt die Klasse erst in seinem
          // eigenen Effekt. Beides ist idempotent — next-themes schreibt gleich
          // denselben Wert nochmal.
          document.documentElement.classList.toggle("dark", mode === "dark");
          // next-themes setzt `style.colorScheme` erst in seinem eigenen Lauf.
          // Bliebe es uns ueberlassen, stuende es nach dem Umschalten kurz noch
          // auf dem alten Wert und alle nativ gemalten Teile — Scrollbalken,
          // Formularelemente, Standard-Rahmen — wuerden ein zweites Mal neu
          // gezeichnet, nachdem die Animation laengst durch ist.
          document.documentElement.style.colorScheme = mode === "dark" ? "dark" : "light";
          // Den Modus mitschreiben, nicht nur die Farbvariante. `resolveInitialTheme`
          // liest beim SSR GENAU dieses Cookie; ohne den Schreibvorgang blieb es
          // auf dem Wert des Onboardings stehen (oder fehlte ganz), und jede
          // Navigation rief die Seite im alten Modus auf. Der Client zog danach
          // per localStorage nach -- sichtbar als Umspringen, und bei fehlendem
          // Cookie ueberschrieb der SSR-Startwert sogar die Wahl des Nutzers.
          //
          // Synchron hier und nicht erst im Nach-Animations-Callback: bricht die
          // Welle ab, ist der Modus trotzdem gewechselt und muss auch gemerkt sein.
          writeThemeCookie(THEME_COOKIE, mode);
          applyThemeVars(colorThemeId, mode === "dark", customOverrides);
          // `flushSync`, weil dieser Callback die Schnappschuss-Grenze IST:
          // startViewTransition haelt davor den alten Zustand fest und danach den
          // neuen. Ein normales setState landete erst im naechsten Render — also
          // nach beiden Aufnahmen — und alles, was daran haengt, spraenge erst
          // nach der Animation um. Mit flushSync liegt der Wechsel exakt auf der
          // Grenze und wischt mit durch.
          flushSync(() => setAppliedDark(mode === "dark"));
        }, originEl,
          themeVarsFor(colorThemeId, mode === "dark", customOverrides),
          mode === "dark",
          // ERST NACH der Animation. `setTheme` kippt `resolvedTheme` und loest
          // damit eine Kette aus, die den Hauptthread blockiert:
          //   · applyThemeVars ein ZWEITES Mal (s. Effekt unten) — ~200
          //     CSS-Variablen auf documentElement, also Style-Neuberechnung des
          //     gesamten Dokuments
          //   · next-themes' disableTransitionOnChange — globales
          //     `transition:none`, ERZWUNGENER Reflow, Ruecknahme nach 1 ms
          //   · scheduleSave() Richtung Supabase
          //   · Re-Render aller useTheme()-Konsumenten
          // Die clip-path-Animation der View-Transition wird vom Hauptthread
          // getrieben. Lief das mitten hinein, wurden ihre ersten Frames nie
          // gezeichnet und der Kreis erschien erst auf halbem Weg.
          //
          // Optisch fehlt hier nichts: Klasse, colorScheme und Variablen stehen
          // synchron im Callback oben — next-themes erfaehrt es nur spaeter.
          () => {
            setTheme(mode);
            flushPendingChange();
          });
      };
      // Waehrend einer laufenden Welle NICHT erneut umschalten (s. `setColorTheme`
      // fuer die ausfuehrliche Begruendung des Doppelklick-Bugs). Statt zu
      // verwerfen, wird der Wunsch gemerkt und laeuft automatisch, sobald die
      // laufende Welle fertig ist.
      if (isThemeTransitionBusy()) {
        pendingChangeRef.current = run;
        return;
      }
      run();
    },
    [setTheme, colorThemeId, customOverrides, pendingMode, resolvedTheme, flushPendingChange]
  );

  // Wie beim Farb-Theme: lokal anwenden, in Cookie+localStorage schreiben (cross-App)
  // UND in die DB (cross-Device + Realtime-Push in bereits offene Tabs anderer Apps).
  const setThemeTransition = useCallback(
    (v: ThemeTransition) => {
      setThemeTransitionState(v);
      transitionRef.current = v;
      writeThemeTransition(v);
      saveRef.current.themeTransition = v;
      scheduleSave();
    },
    [scheduleSave]
  );

  const setOverride = useCallback(
    (mode: "light" | "dark", key: string, value: string) => {
      setCustomOverrides((prev) => {
        const next: ThemeOverrides = {
          ...prev,
          [mode]: { ...prev[mode], [key]: value },
        };
        merkeOverrides(next);
        saveRef.current.overrides = next;
        scheduleSave();
        return next;
      });
    },
    [scheduleSave]
  );

  const resetOverrides = useCallback(() => {
    const empty: ThemeOverrides = { light: {}, dark: {} };
    setCustomOverrides(empty);
    // Die Saat gehoert dazu: sonst behauptet sie die zurueckgesetzte Wahl bis
    // zum naechsten Neuladen weiter.
    setSaatHuelle(null);
    localStorage.removeItem(LS_OVERRIDES_KEY);
    deleteThemeCookie(THEME_OVERRIDES_COOKIE);
    document.documentElement.style.removeProperty("--font-geist-sans");
    saveRef.current.overrides = empty;
    scheduleSave();
  }, [scheduleSave]);

  /**
   * Die geltende Huelle: was im Browser steht, sonst die Saat vom Server.
   *
   * Gelesen wird aus BEIDEN Modi mit `dark` zuerst — geschrieben wird immer in
   * beide, aber ein Konto aus der Zeit davor koennte nur einen haben, und eine
   * halbe Wahl ist besser als keine.
   */
  const huelle = useMemo(
    () =>
      liesHuelle(customOverrides.dark) ??
      liesHuelle(customOverrides.light) ??
      saatHuelle,
    [customOverrides, saatHuelle],
  );

  /**
   * ALLE FUENF SCHALTER IN EINEM ZUG, IN BEIDE MODI.
   *
   * Nicht fuenfmal `setOverride`: das waeren fuenf Speicherlaeufe und vier
   * Zwischenzustaende, in denen die Schale schon gewechselt hat und die Zier
   * noch nicht — und die Zwischenzustaende landen ueber `scheduleSave` auch in
   * `user_preferences`.
   */
  const setHuelle = useCallback(
    (wahl: HuellenEinstellung) => {
      const eintraege = schreibHuelle(wahl);
      setSaatHuelle(wahl);
      setCustomOverrides((prev) => {
        const next: ThemeOverrides = {
          light: { ...prev.light, ...eintraege },
          dark: { ...prev.dark, ...eintraege },
        };
        merkeOverrides(next);
        saveRef.current.overrides = next;
        scheduleSave();
        return next;
      });
    },
    [scheduleSave],
  );

  // Persistenz rein ueber Cookie (parent-domain, cross-App/Subdomain) +
  // localStorage. Cross-Tab-Sync via Focus-Recheck + storage-Listener (oben).
  const setLogoHoverAnim = useCallback((on: boolean) => {
    setLogoHoverAnimState(on);
    localStorage.setItem(LS_LOGO_HOVER_KEY, on ? "1" : "0");
    writeThemeCookie(LOGO_HOVER_COOKIE, on ? "1" : "0");
  }, []);

  // Nur Cookie, kein localStorage: der Wert wird ausschliesslich beim SSR
  // gelesen, und dort kommt localStorage nicht an. Ein zweiter Speicher waere
  // eine zweite Wahrheit ohne Leser.
  const setSymbolStil = useCallback((stil: SymbolStil) => {
    setSymbolStilState(stil);
    writeThemeCookie(SYMBOL_STIL_COOKIE, stil);
  }, []);

  const setVoiceOrb = useCallback((cfg: VoiceOrbConfig) => {
    setVoiceOrbState(cfg);
    const roh = JSON.stringify(cfg);
    localStorage.setItem(LS_VOICE_ORB_KEY, roh);
    writeThemeCookie(VOICE_ORB_COOKIE, roh);
    // Zusaetzlich serverseitig: der Cookie loest cross-App, aber nicht
    // cross-Device — und ein geleerter Browser nahm die Wahl bisher mit.
    saveRef.current.voiceOrb = cfg;
    scheduleSave();
  }, [scheduleSave]);

  return (
    <ColorThemeContext.Provider
      value={{
        colorThemeId,
        setColorTheme,
        customOverrides,
        setOverride,
        resetOverrides,
        huelle,
        setHuelle,
        logoHoverAnim,
        symbolStil,
        setSymbolStil,
        voiceOrb,
        setVoiceOrb,
        setLogoHoverAnim,
        themeTransition,
        setThemeTransition,
        setMode,
        pendingMode,
        appliedDark,
        designPanelOpen,
        setDesignPanelOpen,
        registerDesignPanelHost,
        designPanelAvailable: designPanelHosts > 0,
      }}
    >
      {children}
    </ColorThemeContext.Provider>
  );
}

/**
 * Hell/Dunkel-Setter mit Übergangseffekt — und ohne Provider-Zwang.
 *
 * Umschalter sitzen auch auf Seiten, die NICHT vom ColorThemeProvider umschlossen
 * sind (Login, Rechtstexte, eingebettete Ansichten). `useColorTheme()` würde dort
 * werfen und die Seite weiß machen. Dieser Hook fällt still auf next-themes
 * zurück: kein Effekt, aber der Wechsel funktioniert.
 */
export function useThemeModeSetter(): (mode: ThemeMode, originEl?: Element | null) => void {
  const ctx = useContext(ColorThemeContext);
  const { setTheme } = useTheme();
  return useCallback(
    (mode: ThemeMode, originEl?: Element | null) => {
      if (ctx?.setMode) ctx.setMode(mode, originEl);
      else setTheme(mode);
    },
    [ctx, setTheme]
  );
}

/**
 * Der Modus, den die Oberflaeche ANZEIGEN soll: das laufende Ziel, solange eine
 * Umschaltung unterwegs ist, sonst der aufgeloeste Wert von next-themes.
 * Ohne Provider faellt es still auf next-themes zurueck.
 */
export function useEffectiveThemeMode(): ThemeMode | undefined {
  const ctx = useContext(ColorThemeContext);
  const { resolvedTheme } = useTheme();
  const resolved = resolvedTheme === "dark" || resolvedTheme === "light" ? resolvedTheme : undefined;
  return ctx?.pendingMode ?? resolved;
}

/**
 * Der Modus, wie er GERADE AUF DEM DOKUMENT liegt.
 *
 * Fuer Oberflaechen, die WAEHREND der Uebergangsanimation sichtbar bleiben und
 * deren Farben aus JavaScript kommen statt aus CSS-Variablen — allen voran die
 * Farbkacheln im Design-Panel.
 *
 * Unterschied zu `useEffectiveThemeMode()`: dessen `pendingMode` kippt VOR dem
 * Start der Animation. Was daran haengt, steht schon im alten Schnappschuss neu
 * da und springt sichtbar um, statt mitzuwischen. Dieser Wert kippt im
 * `apply()` — exakt auf der Grenze zwischen altem und neuem Schnappschuss.
 *
 * Fuer reine Zustandsanzeigen (aktives Segment, Umschalter-Icon) bleibt
 * `useEffectiveThemeMode()` richtig: dort ist die sofortige Rueckmeldung
 * wichtiger als das Mitwischen.
 */
export function useAppliedThemeMode(): ThemeMode {
  const ctx = useContext(ColorThemeContext);
  const { resolvedTheme } = useTheme();
  if (ctx?.appliedDark != null) return ctx.appliedDark ? "dark" : "light";
  return resolvedTheme === "dark" ? "dark" : "light";
}

/**
 * Zustand des Design-Schnellpanels — ohne Provider-Zwang.
 *
 * `ThemeToggleButton` sitzt auch auf Seiten OHNE `ColorThemeProvider`
 * (Rechtstexte, eingebettete Ansichten). `useColorTheme()` wuerde dort werfen
 * und die Seite weiss machen. Analog zu `useThemeModeSetter()` faellt dieser
 * Hook still auf ein geschlossenes Panel mit No-Op-Setter zurueck.
 *
 * `available`/`registerHost`: der Rechtsklick im Schalter darf das native
 * Kontextmenue nur unterdruecken, wenn irgendwo tatsaechlich ein Renderer
 * (TopNav/HubLauncher) gemountet ist — sonst waere der Rechtsklick eine
 * Sackgasse (StatusDashboard, ApiExplorer, MobileNavAccountFooter rendern den
 * Schalter ohne Panel). Ohne Provider bleibt `available` immer `false` und
 * `registerHost` ein No-Op mit No-Op-Cleanup.
 */
export function useDesignPanel(): {
  open: boolean;
  setOpen: (v: boolean) => void;
  available: boolean;
  registerHost: () => () => void;
} {
  const ctx = useContext(ColorThemeContext);
  return {
    open: ctx?.designPanelOpen ?? false,
    setOpen: ctx?.setDesignPanelOpen ?? (() => undefined),
    available: ctx?.designPanelAvailable ?? false,
    registerHost: ctx?.registerDesignPanelHost ?? (() => () => undefined),
  };
}

/**
 * Wie `useColorTheme`, wirft aber nicht ohne Provider. Fuer Bausteine, die auch
 * ausserhalb der App laufen — der `VoiceVisualizer` steckt im Testanruf, in
 * Vorschauen und in Storybook-artigen Umgebungen.
 */
export function useColorThemeOptional(): ColorThemeContextValue | null {
  return useContext(ColorThemeContext);
}

export function useColorTheme(): ColorThemeContextValue {
  const ctx = useContext(ColorThemeContext);
  if (!ctx)
    throw new Error("useColorTheme must be used within ColorThemeProvider");
  return ctx;
}

export type { ThemeOverrides, ColorThemeContextValue };
